import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Save, Building2, CreditCard } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentSettings() {
  const [loading, setLoading] = useState(false);
  const [bankDetails, setBankDetails] = useState({
    account_holder_name: "",
    account_number: "",
    bank_name: "",
    ifsc_code: "",
    swift_code: "",
  });
  const [stripeSettings, setStripeSettings] = useState({
    test_publishable_key: "",
    test_secret_key: "",
    production_publishable_key: "",
    production_secret_key: "",
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/settings/payment`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.data.bank_details) {
        setBankDetails(response.data.bank_details);
      }
      if (response.data.stripe_settings) {
        setStripeSettings(response.data.stripe_settings);
      }
    } catch (error) {
      toast.error("Failed to fetch payment settings");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/settings/payment`,
        {
          bank_details: bankDetails,
          stripe_settings: stripeSettings,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success("Payment settings updated successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update payment settings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8" data-testid="payment-settings-page">
      <div className="max-w-4xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-800 mb-2">Payment Settings</h1>
          <p className="text-slate-600">Configure your bank account and payment gateway credentials</p>
        </div>

        <form onSubmit={handleSubmit}>
          <Tabs defaultValue="bank" className="space-y-6">
            <TabsList className="grid w-full grid-cols-2 max-w-md">
              <TabsTrigger value="bank" className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Bank Details
              </TabsTrigger>
              <TabsTrigger value="stripe" className="flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Stripe
              </TabsTrigger>
            </TabsList>

            <TabsContent value="bank">
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Bank Account Information
                  </CardTitle>
                  <CardDescription>Add your bank account details for direct transfers</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="account_holder_name">Account Holder Name</Label>
                      <Input
                        id="account_holder_name"
                        value={bankDetails.account_holder_name}
                        onChange={(e) => setBankDetails({ ...bankDetails, account_holder_name: e.target.value })}
                        placeholder="Enter account holder name"
                        className="h-11 border-slate-300"
                        data-testid="account-holder-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="account_number">Account Number</Label>
                      <Input
                        id="account_number"
                        type="password"
                        value={bankDetails.account_number}
                        onChange={(e) => setBankDetails({ ...bankDetails, account_number: e.target.value })}
                        placeholder="Enter account number"
                        className="h-11 border-slate-300"
                        data-testid="account-number-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bank_name">Bank Name</Label>
                    <Input
                      id="bank_name"
                      value={bankDetails.bank_name}
                      onChange={(e) => setBankDetails({ ...bankDetails, bank_name: e.target.value })}
                      placeholder="Enter bank name"
                      className="h-11 border-slate-300"
                      data-testid="bank-name-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="ifsc_code">IFSC Code</Label>
                      <Input
                        id="ifsc_code"
                        value={bankDetails.ifsc_code}
                        onChange={(e) => setBankDetails({ ...bankDetails, ifsc_code: e.target.value })}
                        placeholder="Enter IFSC code"
                        className="h-11 border-slate-300"
                        data-testid="ifsc-code-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="swift_code">SWIFT/BIC Code (Optional)</Label>
                      <Input
                        id="swift_code"
                        value={bankDetails.swift_code}
                        onChange={(e) => setBankDetails({ ...bankDetails, swift_code: e.target.value })}
                        placeholder="Enter SWIFT code"
                        className="h-11 border-slate-300"
                        data-testid="swift-code-input"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="stripe">
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Stripe Configuration
                  </CardTitle>
                  <CardDescription>Configure your Stripe test and production API keys</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                      Test Environment
                    </h4>

                    <div className="space-y-2">
                      <Label htmlFor="test_publishable_key">Test Publishable Key</Label>
                      <Input
                        id="test_publishable_key"
                        value={stripeSettings.test_publishable_key}
                        onChange={(e) => setStripeSettings({ ...stripeSettings, test_publishable_key: e.target.value })}
                        placeholder="pk_test_..."
                        className="h-11 border-slate-300 font-mono text-sm"
                        data-testid="test-publishable-key-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="test_secret_key">Test Secret Key</Label>
                      <Input
                        id="test_secret_key"
                        type="password"
                        value={stripeSettings.test_secret_key}
                        onChange={(e) => setStripeSettings({ ...stripeSettings, test_secret_key: e.target.value })}
                        placeholder="sk_test_..."
                        className="h-11 border-slate-300 font-mono text-sm"
                        data-testid="test-secret-key-input"
                      />
                    </div>
                  </div>

                  <div className="border-t border-slate-200 pt-6 space-y-4">
                    <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      Production Environment
                    </h4>

                    <div className="space-y-2">
                      <Label htmlFor="production_publishable_key">Production Publishable Key</Label>
                      <Input
                        id="production_publishable_key"
                        value={stripeSettings.production_publishable_key}
                        onChange={(e) =>
                          setStripeSettings({ ...stripeSettings, production_publishable_key: e.target.value })
                        }
                        placeholder="pk_live_..."
                        className="h-11 border-slate-300 font-mono text-sm"
                        data-testid="prod-publishable-key-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="production_secret_key">Production Secret Key</Label>
                      <Input
                        id="production_secret_key"
                        type="password"
                        value={stripeSettings.production_secret_key}
                        onChange={(e) => setStripeSettings({ ...stripeSettings, production_secret_key: e.target.value })}
                        placeholder="sk_live_..."
                        className="h-11 border-slate-300 font-mono text-sm"
                        data-testid="prod-secret-key-input"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end mt-6">
            <Button
              type="submit"
              disabled={loading}
              className="bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white font-semibold px-6"
              data-testid="save-button"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}